import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { ContactSupportRequestState } from 'communication/types/ContactSupportRequest';
import { useGetSupportState } from 'app/contactSupport/store/hooks';
import { Button } from '@instinet-analytics/semantic-ui-components';
import { TextAreaInput } from '@instinet-analytics/semantic-ui-components';
import './LoadState.scss';
import { isSupportVisPopup } from 'widgets/supportVisualisation/SupportVisualisationPopup/popup';
import { useWidgetContext } from 'widgets/WidgetContext';
import { useSupportVisualisationPopupChild } from 'widgets/supportVisualisation/SupportVisualisationPopup/SupportVisualisationPopupService';

export interface LoadedState {
    state: ContactSupportRequestState;
    source: string;
}
export interface LoadStateProps {
    onLoadState: (loadedState: LoadedState) => void;
}

export function LoadState(props: LoadStateProps) {
    const { onLoadState } = props;
    const [pastedJson, setPastedJson] = useState('');
    const fileInputRef = useRef<HTMLInputElement>(null);
    const popupChild = useSupportVisualisationPopupChild();
    const { widgetKey } = useWidgetContext();

    const getSupportState = useGetSupportState();

    const isPopup = isSupportVisPopup();

    useEffect(() => {
        if (!popupChild) {
            return;
        }

        const key = 'LoadState-' + widgetKey;
        popupChild.subscribeReceivedState(key, (state) => {
            state && onLoadState({ source: 'Parent', state });
        });

        popupChild.firstTimeStateRequest();

        return () => {
            popupChild.unsubscribeReceivedState(key);
        };
    }, [popupChild, widgetKey, onLoadState]);

    const getParent = () => {
        if (popupChild) {
            popupChild.requestParentState();
        }
    };

    const loadState = (source: string, state: ContactSupportRequestState) => {
        onLoadState({
            state,
            source,
        });
    };

    const useCurrent = () => {
        const state = getSupportState();
        loadState('Current state', state);
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        const fileReader = new FileReader();
        if (e.target.files) {
            const file = e.target.files[0];
            const fileName = file.name;
            fileReader.readAsText(file, 'UTF-8');
            fileReader.onload = (e) => {
                loadState(
                    'From ' + fileName,
                    JSON.parse(
                        e!.target!.result as string
                    ) as ContactSupportRequestState
                );
            };
        }
    };

    const pasteJson = () => {
        loadState('From pasted JSON', JSON.parse(pastedJson));
    };

    return (
        <div className="LoadState">
            <div className="buttons">
                {isPopup && (
                    <Button primary onClick={getParent}>
                        Get Parent State
                    </Button>
                )}
                {!isPopup && (
                    <Button primary onClick={useCurrent}>
                        Use Current State
                    </Button>
                )}

                <Button
                    primary
                    onClick={() =>
                        fileInputRef.current && fileInputRef.current.click()
                    }
                >
                    Upload File
                </Button>
                <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    accept="*.json"
                    onChange={handleChange}
                />

                <Button primary onClick={pasteJson}>
                    Use Pasted JSON
                </Button>
            </div>
            <TextAreaInput
                className="paste-json"
                value={pastedJson}
                onChange={(c) => setPastedJson(c)}
                placeholder={'Paste JSON here'}
            />
        </div>
    );
}
